import { Injectable } from '@angular/core';
import { AsyncSubject, Observable } from 'rxjs';

import { CredentialsService } from './credentials.service';
import { HttpQueryInterface, HttpRequestType, HttpService } from '@shared/http/http.service';
import { ParticipantModel } from '@shared/models/participant.model';
import { HttpErrorResponse } from '@angular/common/http';
import { Params, Router } from '@angular/router';

import { ParticipantService } from '@shared/services/participant/participant.service';
import { QueryParamsLogin } from '@app/auth/login/login.component';
import { getRoutePath, PlRoute } from '@app/utils/routes';
import { UrlEndpoint } from '@app/utils/url';
import { environment } from '@src/environments/environment';

export interface LoginContext {
  username: string;
  password: string;
}

export interface AuthenticateResponse {
  token: `Bearer ${string}`;
  user: ParticipantModel;
}

export interface SentPasswordResetResponse {
  status: 'email sent ok';
}

export interface CreatePasswordResponse {
  status: 'OK';
  message: 'Password has been reset.';
}

export enum AuthenticateErrors {
  invalidCredentials = 'Invalid credentials',
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(
    private credentialsService: CredentialsService,
    private httpService: HttpService,
    private participantService: ParticipantService,
    private router: Router,
  ) {}

  public login(context: LoginContext): Observable<boolean> {
    const subject: AsyncSubject<boolean> = new AsyncSubject();

    const requestOptions: HttpQueryInterface = {
      type: HttpRequestType.POST,
      url: UrlEndpoint.authenticate,
      body: {
        email: context.username,
        password: context.password,
      },
    };

    this.httpService.request(requestOptions).subscribe({
      next: async (data: AuthenticateResponse) => {
        try {
          const participant: ParticipantModel = data.user;
          if (participant.cohorts[0].noCouchDb === 0) {
            location.href = `${environment.oldProgramUrl}?token=${data.token}`;
            this.credentialsService.setToken(null);
            this.participantService.setParticipant(null);
            subject.next(true);
            subject.complete();
          } else {
            await this.storeAuthResponseAndRetrieveMenu(participant, data.token);
            subject.next(true);
            subject.complete();
          }
        } catch (err) {
          subject.error(err);
        }
      },
      error: (err: HttpErrorResponse) => {
        subject.error(err);
      },
    });

    return subject;
  }

  private async storeAuthResponseAndRetrieveMenu(participant: ParticipantModel, token: string): Promise<void> {
    this.credentialsService.setToken(token);
    this.participantService.setParticipant(participant);
    await this.participantService.retrieveMenu();
  }

  public logout(queryParamsLogin?: QueryParamsLogin): void {
    this.credentialsService.setToken(null);
    this.participantService.setParticipant(null);
    this.participantService.setMenu(null);

    const queryParams: Params | null = queryParamsLogin ? queryParamsLogin : null;
    this.router.navigate([getRoutePath(PlRoute.login)], { replaceUrl: false, queryParams });
  }

  public forgotPassword(email: string): Observable<SentPasswordResetResponse> {
    const subject: AsyncSubject<SentPasswordResetResponse> = new AsyncSubject();
    const requestOptions: HttpQueryInterface = {
      type: HttpRequestType.POST,
      url: `/password/email`,
      body: {
        email,
      },
    };

    this.httpService.request(requestOptions).subscribe({
      next: async (data: SentPasswordResetResponse) => {
        subject.next(data);
        subject.complete();
      },
      error: (err: HttpErrorResponse) => {
        subject.error(err);
      },
    });

    return subject;
  }

  public setupPassword(creation: boolean, password: string, token: string): Observable<void> {
    const subject: AsyncSubject<void> = new AsyncSubject();
    const requestOptions: HttpQueryInterface = {
      type: HttpRequestType.POST,
      url: creation ? `/password/creation` : `/password/reset`,
      body: {
        password,
        token,
      },
    };

    this.httpService.request(requestOptions).subscribe({
      next: async (data: AuthenticateResponse | CreatePasswordResponse) => {
        if (!creation) {
          const resetPasswordResponse: AuthenticateResponse = data as AuthenticateResponse;
          await this.storeAuthResponseAndRetrieveMenu(resetPasswordResponse.user, resetPasswordResponse.token);
        }
        subject.next();
        subject.complete();
      },
      error: (err: HttpErrorResponse) => {
        subject.error(err);
      },
    });

    return subject;
  }
}
